import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Form } from '@ant-design/compatible';
import type { WrappedFormUtils } from '@ant-design/compatible/lib/form/Form';
import { useCouponForm } from '../../../hooks/coupon/useCouponForm';
import { useCouponFormStore } from '../../../store/couponFormStore';
import CouponSaveNavbar from '../../coupon/save/CouponSaveNavbar';
import Tabs from '../../../components/Tabs';
import CouponSaveInfo from '../../coupon/save/CouponSaveInfo';
import CouponSaveProductLinkInfo from '../../coupon/save/CouponSaveProductLinkInfo';
import CouponSaveBrandLinkInfo from '../../coupon/save/CouponSaveBrandLinkInfo';
import {
  COUPON_AVAILABLE_TYPE,
  COUPON_BRAND_LINK_INFO_TAB_INDEX,
  COUPON_PRODUCT_LINK_INFO_TAB_INDEX,
} from '../../../constants/service/app/couponConstants';
import {
  COUPON_PRODUCT_ITEM_LIST_FILTER_OPTION_MODEL,
  COUPON_PRODUCT_ITEM_LIST_SORT_OPTION_MODEL,
  COUPON_BRAND_ITEM_LIST_FILTER_OPTION_MODEL,
} from '../../../store/modules/coupon/models';
import { _LIST_PAGINATION_MODEL } from '../../../store/modules/app/models';
import { CouponSaveWrapStyle } from './style';

type CouponSaveWrapProps = {
  mode: 'create' | 'edit' | 'copy';
  form: WrappedFormUtils;
};

const CouponSaveWrapInner = (props: CouponSaveWrapProps) => {
  const { mode, form } = props;
  const { id } = useParams<{ id: string }>();
  const couponId = id ? Number(id) : undefined;

  const {
    couponCreate,
    setData,
    toggleModal,
    getProductSubCategoryList,
    handleSubmit,
    handleAvailableTypeChange,
    handlePeriodTypeChange,
    handleGenerateLimitChange,
    setExclude24k,
    getCouponProductItemList,
    getCouponBrandItemList,
    selectCouponProductItemList,
    selectCouponBrandItemList,
    getCouponProductItemListExcelDownload,
    getCouponBrandItemListExcelDownload,
    downloadSelectedCouponProductItemListExcel,
    downloadSelectedCouponBrandItemListExcel,
    checkValidCouponProductItemListExcelUpload,
    checkValidCouponBrandItemListExcelUpload,
  } = useCouponForm(mode, couponId);

  const {
    currentTabKey,
    couponInfo,
    couponBrandLinkInfo,
    couponProductLinkInfo,
    mainProductCategorySelectList,
    subProductCategorySelectList,
  } = couponCreate;

  const handleTabChange = (tabKey: string) => {
    setData({
      path: ['couponCreate', 'currentTabKey'],
      value: tabKey,
    });
  };

  /** 탭 변경 시 상품/브랜드 아이템 초기 로드 (레거시 CouponSaveWrap useEffect 재현) */
  useEffect(() => {
    if (String(currentTabKey) === String(COUPON_PRODUCT_LINK_INFO_TAB_INDEX)) {
      const state = useCouponFormStore.getState();
      if (state.couponProductLinkInfo.itemList.list.length === 0) {
        getCouponProductItemList({
          filterOption: COUPON_PRODUCT_ITEM_LIST_FILTER_OPTION_MODEL,
          sortOption: COUPON_PRODUCT_ITEM_LIST_SORT_OPTION_MODEL,
          pagination: _LIST_PAGINATION_MODEL,
          discountType: state.couponInfo.discountType,
          discountAmount: state.couponInfo.discountAmount,
          maxDiscountPrice: state.couponInfo.maxDiscountPrice,
          isSelectedProductClear: true,
        });
      }
    } else if (String(currentTabKey) === String(COUPON_BRAND_LINK_INFO_TAB_INDEX)) {
      const state = useCouponFormStore.getState();
      if (state.couponBrandLinkInfo.itemList.list.length === 0) {
        getCouponBrandItemList({
          filterOption: COUPON_BRAND_ITEM_LIST_FILTER_OPTION_MODEL,
          pagination: _LIST_PAGINATION_MODEL,
          minimumPurchasePrice: state.couponInfo.minimumPurchasePrice,
          discountType: state.couponInfo.discountType,
          discountAmount: state.couponInfo.discountAmount,
          maxDiscountPrice: state.couponInfo.maxDiscountPrice,
          exclude24k: state.couponInfo.exclude24k,
          isSelectedBrandClear: true,
        });
      }
    }
  }, [currentTabKey]);

  /** edit/copy 모드에서 데이터 로드 전까지 렌더링하지 않음 */
  if ((mode === 'edit' || mode === 'copy') && !couponInfo.couponId) {
    return null;
  }

  return (
    <CouponSaveWrapStyle className="coupon-save-wrap">
      {/* 하단 네브바 */}
      <CouponSaveNavbar onSubmit={() => handleSubmit(form)} />

      <Tabs
        activeKey={currentTabKey}
        onChange={handleTabChange}
        list={[
          {
            title: '쿠폰 정보',
            content: (
              <CouponSaveInfo
                form={form}
                toggleModal={toggleModal}
                setData={(data: any) =>
                  setData({
                    path: ['couponCreate', 'couponInfo', ...data.path],
                    value: data.value,
                  })
                }
                couponInfo={couponInfo}
                onAvailableTypeChange={handleAvailableTypeChange}
                onPeriodTypeChange={(periodType: number) => handlePeriodTypeChange(periodType, form)}
                onGenerateLimitChange={(limitType: number) => handleGenerateLimitChange(limitType, form)}
                setExclude24k={setExclude24k}
              />
            ),
          },
          {
            title: '쿠폰 사용 가능한 상품',
            disabled: !(couponInfo.availableType === COUPON_AVAILABLE_TYPE.TARGET_PRODUCT_COUPON),
            content: (
              <CouponSaveProductLinkInfo
                couponInfo={couponInfo}
                couponProductLinkInfo={couponProductLinkInfo}
                mainProductCategorySelectList={mainProductCategorySelectList}
                subProductCategorySelectList={subProductCategorySelectList}
                getCouponProductItemList={getCouponProductItemList}
                getCouponProductItemListExcelDownload={getCouponProductItemListExcelDownload}
                getSelectedCouponProductItemListExcelDownload={downloadSelectedCouponProductItemListExcel}
                checkValidCouponProductItemListExcelUpload={checkValidCouponProductItemListExcelUpload}
                selectCouponProductItemList={selectCouponProductItemList}
                getProductSubCategoryList={getProductSubCategoryList}
              />
            ),
          },
          {
            title: '쿠폰 사용 가능한 브랜드',
            disabled: !(couponInfo.availableType === COUPON_AVAILABLE_TYPE.TARGET_BRAND_COUPON),
            content: (
              <CouponSaveBrandLinkInfo
                couponInfo={couponInfo}
                toggleModal={toggleModal}
                couponBrandLinkInfo={couponBrandLinkInfo}
                getCouponBrandItemList={getCouponBrandItemList}
                getCouponBrandItemListExcelDownload={getCouponBrandItemListExcelDownload}
                getSelectedCouponBrandItemListExcelDownload={downloadSelectedCouponBrandItemListExcel}
                checkValidCouponBrandItemListExcelUpload={checkValidCouponBrandItemListExcelUpload}
                selectCouponBrandItemList={selectCouponBrandItemList}
              />
            ),
          },
        ]}
      />
    </CouponSaveWrapStyle>
  );
};

/** mode별 래퍼 - Form.create() 적용 */
const CouponSaveWrapWithForm = Form.create()((formProps: any) => {
  const { form, mode } = formProps;
  return <CouponSaveWrapInner form={form} mode={mode} />;
}) as unknown as React.ComponentType<{ mode: 'create' | 'edit' | 'copy' }>;

export const CouponCreateWrap = () => <CouponSaveWrapWithForm mode="create" />;
export const CouponEditWrap = () => <CouponSaveWrapWithForm mode="edit" />;
export const CouponCopyWrap = () => <CouponSaveWrapWithForm mode="copy" />;
